import React, { useState, useEffect } from "react";
import { Menu, Pane, Popover, Position, Text, TextInputField } from "evergreen-ui";
import { TundraStorefrontSummary } from "../../../../common/models";
import { ColorPalette } from "../../context/Theme";

export interface StoreIDPickerProps {
    label?: string;
    value: string;
    onSelect: (value: string) => void;
    onInputChange: () => void;
    required?: boolean;
    width?: string;
    className?: string;
    storefronts?: TundraStorefrontSummary[];
    loading?: boolean;
    error?: Error;
}

export const StoreIDPicker = (props: StoreIDPickerProps): JSX.Element => {
    const {
        label = "Existing Store ID",
        value,
        onSelect,
        onInputChange,
        required = false,
        width = "620px",
        className = "",
        storefronts,
        loading,
        error
    } = props;

    const [inputValue, setInputValue] = useState<string>(value);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [hasSelectedFromDropdown, setHasSelectedFromDropdown] = useState<boolean>(false);

    // Update input value when prop value changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Filter storefronts based on input
    const filteredStorefronts = React.useMemo(() => {
        if (!storefronts) {
            return [];
        }

        if (!inputValue.trim()) {
            // Show all storefronts when input is empty (on focus)
            return storefronts;
        }

        const searchTerm = inputValue.toLowerCase();
        return storefronts.filter(storefront =>
            storefront.storefrontId.toLowerCase().includes(searchTerm)
        );
    }, [storefronts, inputValue]);

    // Handle input change
    // This allows the user to type in the input field to filter through the available storefronts
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setIsDropdownOpen(true); // Always show dropdown when typing or clearing
        setHasSelectedFromDropdown(false); // Reset selection flag when typing
        onInputChange(); // Ensure parent can handle any input changes that occur
        if (newValue === "") {
            onSelect(""); // Notify parent to clear selection if input is cleared
        }
    };

    // Handle click - show dropdown with available options
    const handleClick = () => {
        setIsDropdownOpen(true);
    };

    // Handle item selection from dropdown
    const handleItemSelect = (item: TundraStorefrontSummary) => {
        const selectedValue = item.storefrontId;
        setInputValue(selectedValue);
        setIsDropdownOpen(false);
        setHasSelectedFromDropdown(true);
        onSelect(selectedValue);
        onInputChange();
    };

    // Handle focus - show dropdown with available options
    const handleFocus = () => {
        setIsDropdownOpen(true);
    };

    // Handle blur - close dropdown
    const handleBlur = () => {
        // Small delay to allow clicks on menu items
        setTimeout(() => {
            setIsDropdownOpen(false);

            // If user didn't select from dropdown, clear the input value
            if (!hasSelectedFromDropdown) {
                // Only call onSelect if the value actually changed
                if (inputValue !== "") {
                    onSelect("");
                }
                setInputValue("");
            }

            // Reset the selection flag for next interaction
            setHasSelectedFromDropdown(false);
        }, 150);
    };

    if (loading) {
        return (
            <TextInputField
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                label={label}
                required={required}
                width={width}
                className={className}
                placeholder="Loading Store IDs ..."
                disabled={true}
            />
        );
    }

    if (error) {
        return (
            <TextInputField
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                label={label}
                required={required}
                width={width}
                placeholder="Error loading Store IDs. Please refresh the page."
                className={className}
                onChange={handleInputChange}
            />
        );
    }

    const dropdownContent = filteredStorefronts.length > 0 && isDropdownOpen ? (
        <Pane maxHeight={200} width={620} overflowY="auto" borderRadius={10} border={ColorPalette.borderColor} padding={0}>
            {filteredStorefronts.map((storefront, index) => (
                <Menu.Item
                    padding={20}
                    paddingTop={9}
                    paddingBottom={9}
                    height={40}
                    borderBottom={index !== filteredStorefronts.length - 1 && `1px solid ${ColorPalette.borderColor}`}
                    key={storefront.storefrontId}
                    className="jc-store-id-picker-item"
                    onSelect={() => handleItemSelect(storefront)}
                    onMouseDown={(e: React.MouseEvent) => {
                        // Prevent blur event from firing when clicking menu items
                        e.preventDefault();
                    }}
                >
                    <Text fontSize={13}>{storefront.storefrontId}</Text>
                </Menu.Item>
            ))}
        </Pane>
    ) : null;

    return (
        <Pane width={width}>
            <Popover
                position={Position.BOTTOM_LEFT}
                isShown={!!dropdownContent}
                content={dropdownContent}
                minWidth="trigger"
            >
                <TextInputField
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    label={label}
                    required={required}
                    value={inputValue}
                    className={className}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onClick={handleClick}
                    width="100%"
                />
            </Popover>
        </Pane>
    );
};
